import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /**
   * What to render once an error has been caught. Either a static node or a
   * render function that receives the caught error.
   */
  fallback: ReactNode | ((error: Error) => ReactNode);
  /**
   * Called once when an error is caught — used to report a health signal to
   * Datadog RUM so the rollout guardrail can react. Never called for normal
   * payment declines; only for React render/lifecycle crashes and lazy-load
   * failures in the wrapped subtree.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Generic error boundary used to isolate the payment page and the
 * Adyen Drop-in so a crash degrades gracefully (per-user fallback) instead of
 * white-screening, and is reported for the LaunchDarkly Guarded Release.
 *
 * NB: error boundaries only catch errors thrown during render/lifecycle (and
 * lazy-load rejections). Async failures (event handlers, promises, API calls)
 * must still be reported explicitly via reportPaymentHealthError.
 */
export default class PaymentErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    try {
      this.props.onError?.(error, info);
    } catch {
      /* never let reporting throw out of the boundary */
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      return typeof fallback === "function"
        ? (fallback as (error: Error) => ReactNode)(
            this.state.error ?? new Error("Unknown error"),
          )
        : fallback;
    }
    return this.props.children;
  }
}
